import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { getBusiness } from "@/lib/api";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import { CASE } from "@/constants/testIds";
import { ArrowLeft, ExternalLink, Star } from "lucide-react";

import SonsAfter from "@/components/sites/SonsAfter";
import KlassiqueAfter from "@/components/sites/KlassiqueAfter";
import AngelicAfter from "@/components/sites/AngelicAfter";
import WellStreetAfter from "@/components/sites/WellStreetAfter";
import MinsAfter from "@/components/sites/MinsAfter";
import PowerupAfter from "@/components/sites/PowerupAfter";

const AFTERS = {
  "sons": SonsAfter,
  "klassique-barbers": KlassiqueAfter,
  "angelic-flower-co": AngelicAfter,
  "well-street-kitchen": WellStreetAfter,
  "mins-retro": MinsAfter,
  "powerup-electrical": PowerupAfter,
};

export default function CaseStudy() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [b, setB] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    setB(null); setErr(null);
    getBusiness(slug).then((data) => {
      setB(data);
      document.title = `${data.name} — Case Study | The Managerly`;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        const trade = data.trade || "business";
        const borough = data.borough || "London";
        const reviews = data.reviews || "many";
        const rating = data.rating || "5";
        metaDesc.setAttribute("content", `See how The Managerly redesigned a website for ${data.name}, a ${trade} in ${borough} with ${reviews} Google reviews and a ${rating}★ rating.`);
      }
    }).catch(() => setErr("not found"));
  }, [slug, setB, setErr]);

  if (err) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white">
        <Nav />
        <div className="max-w-3xl mx-auto px-4 py-32 text-center">
          <h1 className="font-display text-5xl">Business not found</h1>
          <button onClick={() => navigate("/")} className="mt-6 underline text-[#FF3B30]">← Back to work</button>
        </div>
        <Footer />
      </div>
    );
  }

  if (!b) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white">
        <Nav />
        <div className="max-w-3xl mx-auto px-4 py-32 font-mono text-white/40">loading case study…</div>
      </div>
    );
  }

  const After = AFTERS[slug];

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <Nav />

      {/* Header */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-16">
        <Link to="/" data-testid={CASE.backToWork} className="inline-flex items-center gap-2 font-mono text-xs text-white/50 hover:text-[#FF3B30] mb-12">
          <ArrowLeft className="w-3 h-3" /> ALL WORK
        </Link>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-8">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#FF3B30] mb-4">
              CASE STUDY · {b.borough.toUpperCase()} · {b.trade.toUpperCase()}
            </p>
            <h1 className="font-display text-5xl md:text-7xl lg:text-8xl tracking-tighter leading-[0.95]">
              {b.name}
            </h1>
          </div>
          <div className="md:col-span-4 flex md:justify-end gap-6 font-mono text-xs">
            <div>
              <div className="text-3xl font-bold text-white">{b.rating}<span className="text-[#FF3B30]">★</span></div>
              <div className="text-white/50 uppercase tracking-[0.2em] text-[10px] mt-1">Google rating</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-white">{b.reviews}</div>
              <div className="text-white/50 uppercase tracking-[0.2em] text-[10px] mt-1">Reviews</div>
            </div>
          </div>
        </div>
      </section>

      {/* BEFORE — Terminal/Data block */}
      <section data-testid={CASE.beforeBlock} className="border-y border-white/10 bg-[#0d0d0d]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-4">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 mb-3">BEFORE</p>
              <h2 className="font-display text-4xl md:text-5xl tracking-tight leading-tight">The raw data.</h2>
              <p className="text-white/60 mt-4 text-sm leading-relaxed">
                Pulled live from Google Maps on day of audit.
              </p>
            </div>
            <div className="md:col-span-8">
              <div className="border border-white/10 bg-black font-mono text-sm">
                <div className="flex items-center gap-2 px-4 py-2 border-b border-white/10 text-white/40 text-xs">
                  <span className="w-2 h-2 bg-[#FF3B30] rounded-full"></span>
                  <span className="w-2 h-2 bg-white/20 rounded-full"></span>
                  <span className="w-2 h-2 bg-white/20 rounded-full"></span>
                  <span className="ml-2">google_maps</span>
                </div>
                <div className="p-5 space-y-2 text-white/80">
                  <Row k="name" v={`"${b.name}"`} />
                  <Row k="trade" v={`"${b.trade}"`} />
                  <Row k="borough" v={`"${b.borough}"`} />
                  <Row k="address" v={`"${b.address}"`} />
                  <Row k="phone" v={b.phone ? `"${b.phone}"` : "null"} mute={!b.phone} />
                  <Row k="rating" v={`${b.rating}`} accent />
                  <Row k="reviews" v={`${b.reviews}`} />
                  <Row k="website" v={b.current_url ? `"${b.current_url}"` : "null"} mute={!b.current_url} alert={!b.current_url || b.current_web.includes("Instagram")} />
                </div>
              </div>

              <div className="mt-6 flex items-start gap-3 text-white/70">
                <Star className="w-4 h-4 text-[#FF3B30] mt-1 flex-shrink-0" />
                <p className="text-base md:text-lg leading-relaxed">{b.verdict}</p>
              </div>

              <a
                href={b.google_listing_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={CASE.externalListing}
                className="mt-6 inline-flex items-center gap-2 font-mono text-xs text-white/50 hover:text-[#FF3B30]"
              >
                Verify on Google Maps <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT WE'D BUILD */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#FF3B30] mb-4">THE BRIEF</p>
        <h2 className="font-display text-3xl md:text-5xl tracking-tight leading-tight max-w-3xl mb-10">
          What we'd build for {b.name}.
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {b.what_we_built.map((item, i) => (
            <div key={item} className="border border-white/10 p-6 bg-[#111] hover:border-[#FF3B30] transition-colors">
              <div className="font-mono text-xs text-[#FF3B30] mb-3">0{i + 1}</div>
              <p className="text-white/80 text-sm leading-relaxed">{item}</p>
            </div>
          ))}
        </div>
      </section>

      {/* AFTER — Embedded redesigned site */}
      <section data-testid={CASE.afterBlock} className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-8">
          <div className="flex items-end justify-between mb-6">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#FF3B30] mb-4">AFTER</p>
              <h2 className="font-display text-4xl md:text-6xl tracking-tighter">The website they should have.</h2>
            </div>
            <div className="hidden md:flex items-center gap-2 font-mono text-xs text-white/40">
              <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
              LIVE PROTOTYPE
            </div>
          </div>
          <p className="text-white/60 max-w-2xl">
            Bespoke palette and typography for {b.name}. Scroll through the prototype below — this isn't a screenshot,
            it's the real redesigned site in your browser.
          </p>
        </div>

        {/* Browser chrome wrapper */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="border border-white/15 bg-[#1a1a1a]">
            <div className="flex items-center gap-2 px-4 py-3 border-b border-white/10">
              <span className="w-3 h-3 rounded-full bg-[#FF5F57]"></span>
              <span className="w-3 h-3 rounded-full bg-[#FEBC2E]"></span>
              <span className="w-3 h-3 rounded-full bg-[#28C840]"></span>
              <div className="ml-4 flex-1 font-mono text-xs text-white/50 bg-black/40 px-3 py-1 truncate">
                https://{b.slug}.studio — live prototype by The Managerly
              </div>
            </div>
            <div>{After ? <After b={b} /> : null}</div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center">
        <h3 className="font-display text-3xl md:text-5xl tracking-tighter mb-6">
          Are you {b.name}? <span className="text-[#FF3B30]">This is yours, if you want it.</span>
        </h3>
        <Link to="/#pitch" className="inline-block bg-[#FF3B30] text-white px-8 py-4 font-display font-bold hover:bg-[#E6352B] transition-colors">
          Claim the redesign →
        </Link>
      </section>

      <Footer />
    </div>
  );
}

const Row = ({ k, v, mute, accent, alert }) => (
  <div className="grid grid-cols-12 gap-3">
    <span className="col-span-3 text-white/40">{k}</span>
    <span className="col-span-1 text-white/30">:</span>
    <span className={`col-span-8 break-all ${accent ? "text-[#FF3B30] font-bold" : ""} ${mute ? "text-white/30 italic" : ""} ${alert ? "bg-[#FF3B30]/10 text-[#FF8377] px-1" : ""}`}>{v}</span>
  </div>
);
